/**
 * Renders a message, turning any bare URL or domain in it into a link.
 *
 * Error text from a provider often ends in the one page that fixes it —
 * "add billing at platform.openai.com/settings/organization/billing" — and
 * asking someone to retype that into a browser, from inside a Desk sidebar,
 * is the difference between a fix and a support email.
 */

// Matches a full URL or a bare domain path. The trailing character class
// leaves sentence punctuation outside the link: a full stop after a URL is
// part of the sentence, not of the address.
const URLS = /\b((?:https?:\/\/)?[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s,;)]*[^\s,;).])?)/gi

/**
 * The hosts our own messages send people to.
 *
 * Linking whatever looks like a domain would have turned text we only pass
 * through — a provider's error body, which can quote a ticket — into a link
 * the agent can click from inside a window they trust. An address we did not
 * write stays plain text.
 */
const OURS = [
  'orovim.com',
  'platform.openai.com',
  'console.anthropic.com',
  'console.groq.com',
  'aistudio.google.com',
  'desk.zoho.com',
  'accounts.zoho.com',
]

function ourLink(label: string) {
  const host = label.replace(/^https?:\/\//i, '').split('/')[0].toLowerCase()
  return OURS.some((known) => host === known || host.endsWith(`.${known}`))
}

export function Linked({ text }: { text: string }) {
  const parts: Array<string | { href: string; label: string }> = []
  let last = 0

  for (const m of text.matchAll(URLS)) {
    const label = m[1]
    // An email address is not a link to follow, and "e.g." is not a domain.
    const before = text[m.index - 1]
    if (before === '@' || !/\.[a-z]{2,}/i.test(label) || !ourLink(label)) continue

    if (m.index > last) parts.push(text.slice(last, m.index))
    parts.push({ href: label.startsWith('http') ? label : `https://${label}`, label })
    last = m.index + label.length
  }

  if (last < text.length) parts.push(text.slice(last))

  return (
    <>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          p
        ) : (
          <a key={i} href={p.href} target="_blank" rel="noreferrer noopener">
            {p.label}
          </a>
        ),
      )}
    </>
  )
}
